import { useLazyQuery, useMutation } from '@apollo/client'
import { useEffect, useState, FormEvent } from 'react'
import { Button, Form, Modal, Spinner } from 'react-bootstrap'
import { toast } from 'react-toastify'
import Select from 'react-select'
import { parseId } from '../../../helpers'
import { useDebounce } from '../../../../../_metronic/helpers'
import { GET_ALL_USERS_FILTER } from '../../../gql/queries/usersQuery'
import { UPDATE_COMMISSION_REPORT_DETAIL } from '../../../gql/mutations/commissionReportDetailMutations'
import { IResAllUsers } from '../../oneUser/oneUserTypes'
import { IResUpdateCommissionReportDetail, NodeCommissionReportDetail } from '../../../types/commissionReportDetailApi'

interface IModalAssignAgentProps {
  isOpen: boolean
  onClose: () => void
  infoEdit: NodeCommissionReportDetail | null
  refetch: () => void
}

export const ModalAssignAgent: React.FC<IModalAssignAgentProps> = ({ isOpen, onClose, infoEdit, refetch }) => {
  const [agentId, setAgent] = useState<string | null>(null)
  const [inputSelect, setInputSelect] = useState('')
  const [options, setOptions] = useState<{ label: string; value: string }[]>([])

  const valueDebounced = useDebounce(inputSelect, 400)

  const [gqlGetUsers, { data: dataUsers, loading: loadingUsers }] = useLazyQuery<IResAllUsers, { name: string }>(GET_ALL_USERS_FILTER)
  const [gqlUpdate, { loading, data, error }] = useMutation<IResUpdateCommissionReportDetail>(UPDATE_COMMISSION_REPORT_DETAIL)

  useEffect(() => {
    if (!valueDebounced) return
    gqlGetUsers({ variables: { name: valueDebounced } })
  }, [valueDebounced, gqlGetUsers])

  useEffect(() => {
    if (!dataUsers) return
    setOptions(
      dataUsers.allUsers.edges.map(({ node }) => ({
        label: `${node.firstName} ${node.lastName} - ${node.email}${
          node.npn ? ` (${node.npn})` : ''
        }`,
        value: node.id,
      }))
    )
  }, [dataUsers])

  useEffect(() => {
    if (!data) return
    const res = data.commissionReportDetailUpdateMutation
    if (res?.success) {
      toast.success('Agent updated successfully')
      refetch()
      onClose()
    } else if (res?.errors?.length) {
      toast.error(`Error: ${res.errors[0]?.message}`)
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data])

  useEffect(() => {
    if (error) toast.error(`Error: ${error.message}`)
  }, [error])

  const handleSubmit = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    if (!infoEdit || !agentId) return

    const {
      commissionReport,
      client,
      commissionType,
      memberId,
      rate,
      members,
      status,
      subtotal,
      effectiveMonth,
      effectiveYear,
    } = infoEdit

    gqlUpdate({
      variables: {
        id: parseId(infoEdit.id),
        input: {
          commissionReport: parseId(commissionReport.id),
          client,
          commissionType,
          memberId,
          rate,
          members,
          status,
          subtotal,
          effectiveMonth,
          effectiveYear,
          user: parseId(agentId),
        },
      },
    })
  }

  return (
    <Modal show={isOpen} onHide={onClose} backdrop='static'>
      <Modal.Header closeButton>
        <Modal.Title>Reassign report records to a different agent</Modal.Title>
      </Modal.Header>
      <Form onSubmit={handleSubmit} id='formAssign'>
        <Modal.Body>
          <Form.Group className='mb-3'>
            <Form.Label>Select an agent</Form.Label>
            <Select
              isLoading={loadingUsers}
              placeholder='Type to search...'
              options={options}
              inputValue={inputSelect}
              onInputChange={setInputSelect}
              onChange={selected => setAgent(selected?.value || null)}
            />
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button variant='secondary' size='sm' onClick={onClose}>CLOSE</Button>
          <Button variant='primary' size='sm' type='submit' form='formAssign' disabled={!agentId || loading}>
            {loading && <Spinner size='sm' animation='border' className='me-2' />}SUBMIT
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  )
}
